<template>
    <div id="main">
        <div class="page">
            <div class="tipos-servicos vertical-flow">
                <div class="listing-header">
                    <div class="title-breadcrumb">
                        <h1 class="title">
                            {{ $t("valor-km") }}
                            <b-icon 
                                v-if="!permissao.create || !permissao.update"
                                icon="information" 
                                size="is-small" 
                                v-tippy 
                                :title="$t('sem-permissao-para-todas-as-acoes')"
                            />
                        </h1>
                        <nav class="breadcrumb" aria-label="breadcrumbs">
                            <ul>
                                <li>
                                    <a class="a-inativo">{{
                                        $t("despesas")
                                    }}</a>
                                </li>
                                <li>
                                    <a
                                        @click="
                                            $router.push({
                                                name: 'des-valore-km-list',
                                            })
                                        "
                                        aria-current="page"
                                        >{{ $t("valor-km") }}</a
                                    >
                                </li>
                            </ul>
                        </nav>
                    </div>
                    <div class="header-buttons">
                        <button
                            v-if="permissao.create && permissao.update"
                            class="save-btn"
                            type="button"
                            tag="button"
                            @click="$router.push({ name: 'des-valor-km-new' })"
                        >
                            {{ $t("adicionar") }}
                        </button>
                    </div>
                </div>

                <div class="columns">
                    <div class="column is-5">
                        <search-bar
                            :onSearch="debouncedOnSearch"
                        />
                    </div>
                    <div class="column is-4">
                        <vss-select
                            ref="tipoVeiculoSelect"
                            title="tipo-veiculo"
                            placeholder="selecione"
                            :options="tipoVeiculos"
                            v-model="tipoVeiculo"
                            @input="onTipoVeiculoChanged"
                            :height="40"
                        />
                    </div>
                    <div class="column is-3 situacao-select">
                        <label v-text="$t('situacao')" />
                        <v-select
                            ref="situacaoInput"
                            class="is-100"
                            label="label"
                            :options="situacoes"
                            @input="onSituacaoChanged"
                            v-model="situacao"
                        >
                            <template slot="option" slot-scope="option">
                                {{ $t(option.label) }}
                            </template>
                            <template
                                slot="selected-option"
                                slot-scope="option"
                            >
                                {{ $t(option.label) }}
                            </template>
                            <template slot="no-options">{{
                                $t("sem-resultados")
                            }}</template>
                        </v-select>
                    </div>
                </div>

                <div class="registry-wrapper">
                    <container-loader
                        v-if="isLoading && !$store.getters.isLoading"
                    />
                    <empty-table-message v-if="isEmpty" />
                    <b-table
                        v-else
                        :data="valoredKm"
                        class="data-table"
                        @sort="sortTable"
                        backend-sorting
                    >
                        <b-table-column
                            v-for="(f, i) in fields"
                            :key="i"
                            :field="f.key"
                            :label="$t(f.key)"
                            :width="f.width"
                            :class="{
                                'excluir-field': f.key == 'situacao',
                                'label-km': f.key == 'label',
                            }"
                            :sortable="f.sortable"
                            :custom-sort="f.sortFn"
                            v-slot="props"
                            :visible="f.visible"
                        >
                            <div v-if="f.key == 'label'">
                                <div 
                                    v-if="props.row[f.key] == 1"
                                    class="bar a-km" 
                                    v-tippy 
                                    :title="$t('acerto-padrao')"
                                />
                            </div>
                            <div v-else-if="f.key == 'colaborador'">
                                <pre>{{ props.row[f.key] }}</pre>
                            </div>
                            <div v-else-if="f.key == 'valor'">
                                {{ getDecimal(props.row[f.key] , null, true) }}
                            </div>
                            <div v-else-if="f.key == 'tipoveiculo'">
                                <span
                                    class="icon"
                                    :style="{ color: props.row[f.key].cor }"
                                    v-if="!cleanHide"
                                >
                                    <i
                                        :class="
                                            props.row[f.key].icone.toLowerCase()
                                        "
                                    />
                                </span>
                                {{ props.row[f.key].descricao }}
                            </div>
                            <div v-else-if="f.key == 'vigenciainicio'">
                                <span v-if="props.row[f.key]">
                                    {{ props.row[f.key] | moment("L") }}
                                </span>
                            </div>
                            <div v-else-if="f.key == 'vigenciafim'">
                                <span v-if="props.row[f.key]">
                                    {{ props.row[f.key] | moment("L") }}
                                </span>
                            </div>
                            <div v-else-if="f.key == 'situacao'">
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    v-if="props.row[f.key] == 1"
                                    type="is-success"
                                    size="is-medium"
                                    >{{ $t("ativo") }}</b-tag
                                >
                                <b-tag
                                    class="table-tag table-tag-full-width"
                                    v-else
                                    type="is-danger"
                                    size="is-medium"
                                    >{{ $t("inativo") }}</b-tag
                                >
                            </div>
                            <div v-else>
                                {{ props.row[f.key] }}
                            </div>
                        </b-table-column>
                    </b-table>
                    <PaginationViewer
                        class="mb-0"
                        :resetPagination="pagination.resetPagination"
                        :totalItems="pagination.totalRows"
                        :perPageOptions="pagination.perPageOptions"
                        :optionStarted="pagination.optionStarted"
                        @change="updatePagination"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ContainerLoader from "@/components/content/loader/ContainerLoader";
import ValoresKmService from "@/services/despesas/ValoresKmService";
import TipoVeiculoService from "@/services/despesas/TipoVeiculoService";

export default {
    components: {
        ContainerLoader,
    },
    data() {
        return {
            cleanHide: false,
            tipoVeiculoService: new TipoVeiculoService(
                this.$http,
                this.$router
            ),
            tipoVeiculos: [{ id: 0, label: this.$t("todos") }],
            tipoVeiculo: { id: 0, label: this.$t("todos") },
            valoredKm: [],
            situacoes: [
                { id: 2, label: "todos" },
                { id: 1, label: "ativos" },
                { id: 0, label: "inativos" },
            ],
            situacao: { id: 1, label: "ativos" },
            service: new ValoresKmService(this.$http, this.$router),
            fields: [
                {
                    key: "label",
                    width: 5,
                    sortable: false,
                    sortFn: null,
                    visible: true,
                },
                {
                    key: "tipoveiculo",
                    width: "15rem",
                    sortable: true,
                    sortFn: null,
                    visible: true,
                },
                {
                    key: "valor",
                    width: "8rem",
                    sortable: true,
                    sortFn: null,
                    visible: true,
                },
                {
                    key: "vigenciainicio",
                    width: "10rem",
                    sortable: true,
                    sortFn: null,
                    visible: true,
                },
                {
                    key: "vigenciafim",
                    width: "10rem",
                    sortable: true,
                    sortFn: null,
                    visible: false,
                },
                {
                    key: "colaborador",
                    width: "20rem",
                    sortable: true,
                    sortFn: null,
                    visible: true,
                },
                {
                    key: "situacao",
                    width: 80,
                    sortable: false,
                    sortFn: null,
                    visible: true,
                },
            ],
            pagination: {
                resetPagination: false,
                perPageOptions: [10, 25, 50, 100],
                optionStarted: 0,
                perPage: null,
                totalRows: 0,
                currentPage: 1,
            },
            isLoading: false,
            isEmpty: false,
            debouncedOnSearch: null,
            delete: null,
            queryFilter: "",
            column: "vigenciainicio",
            order: "desc",
            permissao: {},
        };
    },
    created() {
        this.pagination.perPage =
            this.pagination.perPageOptions[this.pagination.optionStarted];
        this.permissao = this.hasPermissionAccess(
            "despesas",
            "cadastros",
            "des-valor-km"
        );
        if (!this.permissao.read) {
            this.$router.push({
                name: "acesso-nao-permitido",
                params: { tela: "valor-km" },
            });
        } else {
            this.debouncedOnSearch = this.debounce(this.onSearch, 500);
            this.findAllTipoVeiculo();
            this.carregarDados();
        }
    },
    watch: {
        situacao: {
            deep: true,
            handler() {
                let vigenciafim = this.fields.filter(
                    (f) => f.key == "vigenciafim"
                )[0];
                vigenciafim.visible =
                    this.situacao.id == 2 || this.situacao.id == 0
                        ? true
                        : false;
            },
        },
    },
    methods: {
        findAllTipoVeiculo() {
            this.tipoVeiculoService
                .findAllCombo("")
                .then(this.mapItemsTipoVeiculo)
                .then((res) => {
                    this.tipoVeiculos.push.apply(this.tipoVeiculos, res);
                })
                .catch((e) => {
                    console.error(e);
                });
        },
        mapItemsTipoVeiculo(body) {
            let list = body.content.map((m) => {
                return {
                    id: m.idtipoveiculo,
                    label: m.descricao,
                    cor: m.cor,
                    icone: m.icone.toLowerCase(),
                };
            });
            return list;
        },
        updatePagination(value) {
            this.pagination.resetPagination = false;
            this.pagination.perPage = value.perPage;
            this.pagination.currentPage = value.currentPage;
            this.carregarDados(value.currentPage);
        },
        carregarDados(currentPage = 1) {
            this.cleanHide = true;
            if (currentPage == 1 && this.pagination.currentPage != 1)
                this.pagination.resetPagination = true;
            if (this.column == "colaborador") this.column = "nome";

            let params = [
                this.queryFilter != "" ? this.queryFilter : ".*",
                this.tipoVeiculo.id,
            ];

            this.isLoading = true;
            this.service
                .findAll(
                    this.column,
                    this.order,
                    currentPage - 1,
                    this.pagination.perPage,
                    this.situacao.id,
                    params
                )
                .then(this.mapItems)
                .then((value) => {
                    this.valoredKm = value;
                    this.isEmpty = this.valoredKm.length <= 0;
                })
                .catch((e) => {
                    this.isEmpty = true;
                    this.$root.showMessage("error", "msg-erro-load");
                    console.error("Erro ao carregar os acertos.");
                })
                .finally(() => {
                    this.isLoading = false;
                    this.cleanHide = false;
                    this.pagination.resetPagination = false;
                });
        },
        mapItems(body) {
            this.pagination.totalRows = body.totalElements;
            let list = body.content.map((m) => {
                return {
                    id: m.idvalorkm,
                    colaborador: m.usuarioadminc.nome,
                    valor: m.valor,
                    vigenciainicio: m.vigenciainicio,
                    vigenciafim: m.vigenciafim,
                    tipoveiculo: m.tipoVeiculo,
                    situacao: m.situacao,
                    label: m.acertoPadrao,
                };
            });
            return list;
        },
        sortTable(field, order) {
            this.column = field;
            this.order = order;
            this.carregarDados();
        },
        onSearch(value) {
            this.queryFilter = this.removeAcentos(value);
            this.queryFilter = this.queryFilter.replace(/[\,]+/g, ".");
            this.carregarDados();
        },
        onSituacaoChanged(val) {
            let temp = null;
            if (val) {
                temp = this.situacoes.filter((v) => v.id == val.id);
                this.situacao = temp[0];
            } else {
                this.situacao = this.situacoes[0];
            }
            this.carregarDados();
        },
        onTipoVeiculoChanged(val) {
            this.carregarDados();
        },
    },
};
</script>

<style lang="scss" scoped>
#main {
    padding: 0 20px;
    height: 100%;

    .page {
        position: relative;
        padding-left: 0;
    }

    .situacao-select div {
        height: 40px;
    }

    .tipos-servicos .search-and-filter .filter-bar {
        width: 31%;
    }

    .label-km {
        padding-left: 0;
        padding-right: 0;
    }
    .bar {
        border-radius: 100px;
        width: 5px;
        height: 26px;
        position: absolute;
        top: -17px;
        &.a-km {
            background-color: #5f971e;
        }
        &.b-km {
            background-color: #891e97;
        }
    }
    .bar-column {
        padding-left: 0.01rem;
        padding-right: 0.01rem;
    }
    @media (max-width: 760px) {
        .label-km {
            padding-left: 5px;
            padding-right: 20px;
            justify-content: left;
        }
    }
}
</style>
